// avatar-creator/ReactionVisual.js
import React, {
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import Lottie from "lottie-react";

import { getSticker } from "../stickerData";

import "./reaction-visual.styles.scss";

const cloneAnimationData = (source) =>
  typeof structuredClone === "function"
    ? structuredClone(source)
    : JSON.parse(JSON.stringify(source));

export default function ReactionVisual({
  reaction = null,
  interactive = true,
}) {
  const sticker = getSticker(reaction);

  /*
   * Some web bundlers expose required JSON as `default`.
   * Mobile normally returns the JSON object directly.
   */
  const rawSource = sticker?.source;
  const source = rawSource?.default || rawSource;

  const {
    size = 1,
    offsetX = 0,
    offsetY = 0,
    rotation = 0,
    delay = 0,
    duration = 0,
    wait = 1000,
    fadeIn = 0,
    fadeOut = 0,
    loop = false,
    hidePeep = false,
    repetition,
    background = "#F5F5F5",
  } = sticker || {};

  const playLimit =
    Number.isInteger(repetition) && repetition > 0
      ? repetition
      : loop
        ? Infinity
        : 1;

  const nativeLoop = hidePeep
    ? playLimit === Infinity
    : loop;

  const animationData = useMemo(
    () => (source ? cloneAnimationData(source) : null),
    [source],
  );

  const visualRef = useRef(null);
  const lottieRef = useRef(null);
  const controlsRef = useRef(null);

  const speed = 0.5;

  useLayoutEffect(() => {
    if (!source || !visualRef.current) {
      return;
    }

    const visual = visualRef.current;

    const naturalDuration =
      (((source.op || 0) - (source.ip || 0)) /
        (source.fr || 60) /
        speed) *
      1000;

    const steps = [
      { opacity: 0, time: delay },
      { opacity: 1, time: fadeIn },
      {
        opacity: 1,
        time:
          duration > 0
            ? duration
            : Math.max(0, naturalDuration - fadeIn),
      },
      { opacity: 0, time: fadeOut },
      { opacity: 0, time: wait },
    ];

    let status = "loading";
    let step = 0;
    let completed = 0;
    let lottieFinished = false;
    let phaseAnimation = null;

    visual.style.opacity = "0";

    function playLottie() {
      lottieFinished = false;
      lottieRef.current?.setSpeed(speed);
      lottieRef.current?.goToAndPlay(0, true);
    }

    function resumeLottie() {
      if (!lottieFinished) {
        lottieRef.current?.play();
      } else if (
        hidePeep &&
        completed < playLimit
      ) {
        playLottie();
      }
    }

    function runStep() {
      const currentStep = steps[step];

      const fromOpacity = Number.parseFloat(
        window.getComputedStyle(visual).opacity,
      );

      phaseAnimation = visual.animate(
        [
          {
            opacity: Number.isFinite(fromOpacity)
              ? fromOpacity
              : 0,
          },
          { opacity: currentStep.opacity },
        ],
        {
          duration: Math.max(0, currentStep.time),
          easing: "linear",
          fill: "forwards",
        },
      );

      phaseAnimation.onfinish = () => {
        if (status !== "playing") {
          return;
        }

        visual.style.opacity = String(
          currentStep.opacity,
        );

        phaseAnimation.cancel();
        phaseAnimation = null;
        step += 1;

        // Full stickers remain visible after fading in.
        if (hidePeep && step === 2) {
          if (
            lottieFinished &&
            completed >= playLimit
          ) {
            status = "finished";
          }

          return;
        }

        // Avatar reactions count completed visibility cycles.
        if (step === 4) {
          lottieRef.current?.pause();
          completed += 1;

          if (completed >= playLimit) {
            status = "finished";
            return;
          }
        }

        // Repeat without applying the initial delay again.
        if (step === steps.length) {
          step = 1;
        }

        if (step === 1) {
          playLottie();
        }

        runStep();
      };
    }

    function start() {
      phaseAnimation?.cancel();
      lottieRef.current?.setSpeed(speed);
      lottieRef.current?.stop();

      visual.style.opacity = "0";

      status = "playing";
      step = 0;
      completed = 0;
      lottieFinished = false;

      runStep();
    }

    controlsRef.current = {
      finish: () => {
        if (
          !hidePeep ||
          playLimit === Infinity ||
          status === "disposed"
        ) {
          return;
        }

        completed += 1;
        lottieFinished = true;

        if (completed >= playLimit) {
          if (step === 2) {
            status = "finished";
          }
        } else if (status === "playing") {
          playLottie();
        }
      },

      toggle: () => {
        if (status === "loading") {
          return;
        }

        if (status === "finished") {
          start();
          return;
        }

        if (status === "paused") {
          status = "playing";
          phaseAnimation?.play();

          if (
            (hidePeep && step === 2) ||
            (step >= 1 && step <= 3)
          ) {
            resumeLottie();
          }
        } else {
          status = "paused";
          phaseAnimation?.pause();
          lottieRef.current?.pause();
        }
      },

      ready: () => {
        if (status === "loading") {
          start();
        }
      },
    };

    if (lottieRef.current?.isLoaded) {
      controlsRef.current.ready();
    }

    return () => {
      status = "disposed";
      controlsRef.current = null;
      phaseAnimation?.cancel();
    };
  }, [
    reaction,
    source,
    delay,
    duration,
    wait,
    fadeIn,
    fadeOut,
    loop,
    hidePeep,
    playLimit,
    nativeLoop,
  ]);

  if (!source) {
    return null;
  }

  return (
    <>
      {hidePeep && (
        <div
          className="reactionVisual__peepCover"
          style={{ backgroundColor: background }}
        />
      )}

      <div
        ref={visualRef}
        className="reactionVisual"
        style={{ backgroundColor: background }}
        aria-hidden="true"
      >
        <div
          className="reactionVisual__animation"
          style={{
            transform: `
              translate(${offsetX}px, ${offsetY}px)
              rotate(${rotation}deg)
              scale(${size})
            `,
          }}
        >
          <Lottie
            key={reaction}
            lottieRef={lottieRef}
            animationData={animationData}
            autoplay={false}
            loop={nativeLoop}
            onDOMLoaded={() =>
              controlsRef.current?.ready()
            }
            onComplete={() =>
              controlsRef.current?.finish()
            }
            rendererSettings={{
              preserveAspectRatio: "xMidYMid meet",
            }}
            style={{
              width: "100%",
              height: "100%",
            }}
          />
        </div>
      </div>

      {interactive && (
        <button
          type="button"
          className="reactionVisual__touchArea"
          onClick={() =>
            controlsRef.current?.toggle()
          }
          aria-label="Pause, resume or replay reaction"
        />
      )}
    </>
  );
}